<script setup lang="ts">
  import { computed } from 'vue'
  import { formatInlineDescription } from '@/utils/formatDescription'

  const props = defineProps<{
    isOpen: boolean,
    description?: string | string[] | { text: string, subtext?: string }[],
  }>()

  const emit = defineEmits<{
    (e: 'update:isOpen', value: boolean): void,
  }>()

  const closeModal = () => {
    emit('update:isOpen', false)
  }

  const formattedDescription = computed(() => formatInlineDescription(props.description))

  const modalHeader = computed(() => {
    if (typeof props.description === 'string') {
      const lines = props.description.split('\n')
      return lines.find(item => !item.trim().startsWith('- ')) || 'Details'
    }
    return 'Details'
  })
</script>

<template>
  <div
    v-if="isOpen"
    class="fixed inset-0 flex justify-end items-end z-50"
    @click="closeModal">
    <div
      class="w-full max-w-md p-6 shadow-lg max-h-[50vh] overflow-y-auto transform transition-transform duration-300 rounded-2xl border border-secondary-2 bg-secondary-1 m-6"
      :class="{ 'translate-y-0 translate-x-0': isOpen, 'translate-y-full translate-x-full': !isOpen }"
      @click.stop>
      <div class="flex justify-between items-center mb-2">
        <svg
          class="h-5 w-5 text-secondary-2"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor">
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <button
          type="button"
          class="text-secondary-2 hover:text-secondary-3"
          @click="closeModal">
          <svg
            class="size-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
      <div class="text-secondary-2">
        <h5 class="mb-2 text-sm">
          {{ modalHeader }}
        </h5>
        <ul class="list-disc pl-5">
          <li
            v-for="(item, index) in formattedDescription"
            :key="index"
            class="text-sm">
            <p>
              {{ item }}
            </p>
            <p
              v-if="Array.isArray(props.description) && typeof props.description[index] === 'object' && props.description[index].subtext"
              class="text-xs mt-1 text-secondary-2">
              {{ props.description[index].subtext }}
            </p>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<style scoped>
.translate-y-full.translate-x-full {
  transform: translateY(100%) translateX(100%);
}

.translate-y-0.translate-x-0 {
  transform: translateY(0) translateX(0);
}

div[role="dialog"] {
  position: fixed;
  bottom: 0;
  right: 0;
}
</style>
